import React, { useState, useMemo, useRef, useEffect, useCallback } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSpinner } from "@fortawesome/free-solid-svg-icons";
import { useTheme } from '../contexts/ThemeContext';
import { useAuth } from '../contexts/AuthContext';
import authService from '../services/authService';
import './Signup.css';

const PENDING_VERIFY_KEY = 'examflip_pending_verify';

function consumePendingVerifyCredentials(email) {
  try {
    const raw = localStorage.getItem(PENDING_VERIFY_KEY);
    if (!raw) return null;
    const data = JSON.parse(raw);
    if (data.email !== email || Date.now() > data.expires) {
      localStorage.removeItem(PENDING_VERIFY_KEY);
      return null;
    }
    localStorage.removeItem(PENDING_VERIFY_KEY);
    return data.password;
  } catch {
    return null;
  }
}

function VerifyEmail() {
  const location = useLocation();
  const navigate = useNavigate();
  const { isDarkMode } = useTheme();
  const { login } = useAuth();

  const params = useMemo(() => {
    // Normalize double question marks (e.g. ??token=...) produced by the email link template
    const normalized = location.search.replace(/^\?+/, '');
    return new URLSearchParams(normalized);
  }, [location.search]);

  const email = useMemo(() => params.get('email') || '', [params]);
  const urlToken = useMemo(() => params.get('token') || '', [params]);

  // Six individual digit inputs
  const [digits, setDigits] = useState(['', '', '', '', '', '']);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [resendLoading, setResendLoading] = useState(false);
  const [resendMessage, setResendMessage] = useState('');
  const [verified, setVerified] = useState(false);
  const autoSubmittedRef = useRef(false);

  const inputRefs = useRef([]);

  const handleDigitChange = (index, value) => {
    const digit = value.replace(/\D/g, '').slice(-1);
    const next = [...digits];
    next[index] = digit;
    setDigits(next);
    if (digit && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !digits[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    const next = [...digits];
    for (let i = 0; i < pasted.length; i++) {
      next[i] = pasted[i];
    }
    setDigits(next);
    const nextFocus = Math.min(pasted.length, 5);
    inputRefs.current[nextFocus]?.focus();
  };

  const code = digits.join('');

  const performAutoLogin = useCallback(async (verifiedEmail) => {
    const password = consumePendingVerifyCredentials(verifiedEmail);
    if (password) {
      try {
        await login(verifiedEmail, password, { rememberMe: true, isNewUser: true });
        // AuthContext.login navigates to /dashboard on success
        return;
      } catch {
        // Fall through to the verified-stay state below.
      }
    }
    // No stored credentials (e.g. user clicked the email link in a different
    // browser) — stay on the page and show a verified confirmation instead of
    // bouncing to /login.
    setVerified(true);
  }, [login]);

  const handleSubmit = useCallback(async (e, overrideCode) => {
    if (e && e.preventDefault) e.preventDefault();
    const submitCode = overrideCode || code;
    if (submitCode.length !== 6) {
      setError('Please enter the complete 6-digit code.');
      return;
    }
    setError('');
    setIsLoading(true);
    try {
      const response = await authService.confirmRegistration(submitCode, { email });
      if (response && response.result && response.result.toLowerCase() !== 'success') {
        const msg = response.message || 'Verification failed. Please try again.';
        throw new Error(msg);
      }
      // Free package is now assigned inside AuthContext.login when isNewUser is true.
      // No localStorage flag needed – this avoids interference with future Stripe upgrades.
      await performAutoLogin(email);
    } catch (err) {
      let errorMessage;
      if (err.data && err.data.result) {
        errorMessage = err.data.message || 'Verification failed. Please try again.';
      } else {
        errorMessage = err.message || 'Verification failed. Please try again.';
      }
      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  }, [code, email, performAutoLogin]);

  // Keep a ref to the latest handleSubmit so the auto-submit timer always calls
  // the up-to-date version without the timer being cancelled by effect re-runs.
  const handleSubmitRef = useRef(handleSubmit);
  useEffect(() => {
    handleSubmitRef.current = handleSubmit;
  }, [handleSubmit]);

  // Auto-fill and auto-submit when token + email are both in the URL.
  // Deps are intentionally only [urlToken, email] so that the setDigits re-render
  // does NOT re-run this effect and cancel the pending timer.
  useEffect(() => {
    if (urlToken.length === 6 && email && !autoSubmittedRef.current) {
      setDigits(urlToken.split(''));
      // Set the guard inside the timer callback (not before). In React 18 StrictMode the
      // effect mounts → unmounts → mounts; if we flipped the ref before scheduling, the
      // first cleanup would cancel the timer and the second mount would see the ref
      // already true and skip — auto-submit would never fire.
      const timer = setTimeout(() => {
        autoSubmittedRef.current = true;
        handleSubmitRef.current(null, urlToken);
      }, 300);
      return () => clearTimeout(timer);
    }
  }, [urlToken, email]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleResend = async () => {
    setResendMessage('');
    setError('');
    setResendLoading(true);
    try {
      await authService.resendActivationEmail(email);
      setResendMessage('A new verification code has been sent to your email.');
    } catch {
      setResendMessage('Failed to resend. Please try again.');
    } finally {
      setResendLoading(false);
    }
  };

  const themeStyles = useMemo(() => ({
    container: {
      background: isDarkMode
        ? 'linear-gradient(180deg, #0e1626 0%, #0b1220 100%)'
        : '#eceff4'
    },
    content: {
      background: isDarkMode
        ? 'linear-gradient(145deg, #151e30 0%, #121a2c 100%)'
        : 'linear-gradient(145deg, #ffffff 0%, #f5f9ff 100%)',
      boxShadow: isDarkMode ? '0 6px 12px rgba(0,0,0,0.3)' : '0 6px 12px rgba(0,0,0,0.1)'
    },
    text: {
      primary: isDarkMode ? '#ffffff' : '#2c3e50',
      secondary: isDarkMode ? '#e1e9ff' : 'rgba(44, 62, 80, 0.8)'
    },
    button: { background: isDarkMode ? '#0074cc' : '#1976d2', color: '#ffffff' },
    link: { color: isDarkMode ? '#90cdf4' : '#3498db' },
    digitBox: {
      width: '48px',
      height: '56px',
      fontSize: '24px',
      fontWeight: '700',
      textAlign: 'center',
      border: isDarkMode ? '2px solid rgba(255,255,255,0.3)' : '2px solid #cbd5e0',
      borderRadius: '8px',
      background: isDarkMode ? 'rgba(255,255,255,0.08)' : '#f7fafc',
      color: isDarkMode ? '#ffffff' : '#2c3e50',
      outline: 'none',
      transition: 'border-color 0.2s',
    }
  }), [isDarkMode]);

  return (
    <div className={`login-page-container ${isDarkMode ? 'dark-mode' : 'light-mode'}`} style={themeStyles.container}>
      <div className="login-container-main" style={themeStyles.content}>
        <div className="login-header">
          <img src={isDarkMode ? '/dark_green.png' : '/white_black.png'} alt="ExamFlip Logo" className="header-logo" />
        </div>

        <div style={{ textAlign: 'center', marginBottom: '24px' }}>
          <h2 style={{ color: themeStyles.text.primary, marginBottom: '8px' }}>
            {verified ? 'Email verified' : 'Verify your email'}
          </h2>
          {verified ? (
            <p style={{ color: themeStyles.text.secondary, fontSize: '14px', lineHeight: '1.6' }}>
              <strong style={{ color: themeStyles.text.primary }}>{email}</strong> has been verified.<br />
              You can now sign in to your account.
            </p>
          ) : (
            <>
              <p style={{ color: themeStyles.text.secondary, fontSize: '14px', lineHeight: '1.6' }}>
                We've sent a 6-digit verification code to<br />
                <strong style={{ color: themeStyles.text.primary }}>{email}</strong>
              </p>
              {urlToken.length === 6 ? (
                <p style={{ color: themeStyles.text.secondary, fontSize: '13px', marginTop: '8px' }}>
                  Verifying your email automatically&hellip;
                </p>
              ) : (
                <p style={{ color: themeStyles.text.secondary, fontSize: '13px', marginTop: '8px' }}>
                  Please check your inbox (and spam folder) and enter the code below.
                </p>
              )}
            </>
          )}
        </div>

        {verified && (
          <div style={{ textAlign: 'center', marginBottom: '20px' }}>
            <div style={{ color: isDarkMode ? '#68d391' : '#276749', marginBottom: '20px', padding: '14px', backgroundColor: isDarkMode ? 'rgba(72,187,120,0.1)' : 'rgba(72,187,120,0.05)', borderRadius: '8px', border: '1px solid rgba(72,187,120,0.3)', fontSize: '14px' }}>
              Your email has been successfully verified.
            </div>
            <Link
              to={`/login?email=${encodeURIComponent(email)}`}
              className="login-button"
              style={{ background: themeStyles.button.background, color: themeStyles.button.color, display: 'inline-block', textDecoration: 'none', padding: '12px 24px' }}
            >
              Go to sign in
            </Link>
          </div>
        )}

        {!verified && (
        <form onSubmit={handleSubmit} className="login-form">
          <div style={{ display: 'flex', justifyContent: 'center', gap: '10px', marginBottom: '24px' }} onPaste={handlePaste}>
            {digits.map((digit, i) => (
              <input
                key={i}
                ref={el => inputRefs.current[i] = el}
                type="text"
                inputMode="numeric"
                maxLength={1}
                value={digit}
                onChange={e => handleDigitChange(i, e.target.value)}
                onKeyDown={e => handleKeyDown(i, e)}
                disabled={isLoading}
                style={{
                  ...themeStyles.digitBox,
                  borderColor: digit ? (isDarkMode ? '#4299e1' : '#3182ce') : themeStyles.digitBox.borderColor,
                }}
              />
            ))}
          </div>

          {error && (
            <div className="error-message" style={{ color: '#e53e3e', marginBottom: '15px', padding: '12px', backgroundColor: isDarkMode ? 'rgba(229,62,62,0.1)' : 'rgba(229,62,62,0.05)', borderRadius: '8px', border: '1px solid rgba(229,62,62,0.3)', fontSize: '14px', textAlign: 'center' }}>
              {error}
            </div>
          )}

          {resendMessage && (
            <div style={{ color: isDarkMode ? '#68d391' : '#276749', marginBottom: '15px', padding: '12px', backgroundColor: isDarkMode ? 'rgba(72,187,120,0.1)' : 'rgba(72,187,120,0.05)', borderRadius: '8px', border: '1px solid rgba(72,187,120,0.3)', fontSize: '14px', textAlign: 'center' }}>
              {resendMessage}
            </div>
          )}

          <button
            type="submit"
            className="login-button"
            disabled={isLoading || code.length !== 6}
            style={{ background: (isLoading || code.length !== 6) ? '#ccc' : themeStyles.button.background, color: themeStyles.button.color, cursor: (isLoading || code.length !== 6) ? 'not-allowed' : 'pointer' }}
          >
            {isLoading ? (
              <><FontAwesomeIcon icon={faSpinner} spin style={{ marginRight: '8px' }} />Verifying...</>
            ) : 'Verify Email'}
          </button>
        </form>
        )}

        {!verified && (
        <div style={{ textAlign: 'center', marginTop: '20px' }}>
          <p style={{ color: themeStyles.text.secondary, fontSize: '14px', marginBottom: '8px' }}>
            Didn't receive a code?
          </p>
          <button
            onClick={handleResend}
            disabled={resendLoading}
            style={{ background: 'transparent', color: themeStyles.link.color, border: 'none', textDecoration: 'underline', cursor: resendLoading ? 'not-allowed' : 'pointer', fontSize: '14px' }}
          >
            {resendLoading ? 'Sending...' : 'Resend code'}
          </button>
        </div>
        )}

        {!verified && (
        <div className="register-now" style={{ marginTop: '16px' }}>
          <p style={{ color: themeStyles.text.primary }}>
            <Link to="/create-account" style={{ color: themeStyles.link.color }}>← Back to registration</Link>
          </p>
        </div>
        )}
      </div>
    </div>
  );
}

export default VerifyEmail;
